// src/types/enums.ts
var ProductOptionType = {
  // Legacy (pre custom-product-options). Kept so historical rows stay valid.
  STRING: "STRING",
  BOOLEAN: "BOOLEAN",
  NUMBER: "NUMBER",
  SELECT: "SELECT",
  // Custom product options.
  TEXT: "TEXT",
  TEXTAREA: "TEXTAREA",
  CHECKBOX: "CHECKBOX",
  CHECKBOX_GROUP: "CHECKBOX_GROUP",
  RADIO: "RADIO",
  SWATCH: "SWATCH",
  BUTTON_GROUP: "BUTTON_GROUP",
  DATE: "DATE",
  FILE_UPLOAD: "FILE_UPLOAD",
  IMAGE_UPLOAD_SIZED: "IMAGE_UPLOAD_SIZED",
  GANGSHEET_UPLOAD_SIZED: "GANGSHEET_UPLOAD_SIZED",
  CUSTOM_SIZE: "CUSTOM_SIZE",
  INFO: "INFO"
};

// src/product-options/types.ts
import { z } from "zod";
var PRODUCT_OPTIONS_SCHEMA_VERSION = 1;
var PRODUCT_OPTION_CHOICE_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;
var productOptionChoiceSchema = z.object({
  key: z.string().regex(PRODUCT_OPTION_CHOICE_KEY_PATTERN),
  label: z.string().min(1).max(200),
  imageUrl: z.string().max(2e3).nullable().optional(),
  iconUrl: z.string().max(2e3).nullable().optional(),
  colorHex: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  isDefault: z.boolean().default(false),
  /** Per-choice surcharge in minor currency units (overrides option level). */
  priceDeltaCents: z.number().int().min(0).max(1e8).nullable().optional()
});
var productOptionChoicesSchema = productOptionChoiceSchema.array().max(500).superRefine((choices, ctx) => {
  const seen = /* @__PURE__ */ new Map();
  choices.forEach((choice, index) => {
    const previous = seen.get(choice.key);
    if (previous != null) {
      ctx.addIssue({
        code: "custom",
        path: [index, "key"],
        message: `Choice key "${choice.key}" is already used.`
      });
      return;
    }
    seen.set(choice.key, index);
  });
});
var UPLOAD_SOURCES = ["DEVICE", "RECENT", "GALLERY", "PHONE", "AI"];
var SIZE_PRICE_MODES = ["AREA_RATE", "LONGEST_EDGE_RATE", "HEIGHT_RATE", "VARIANT_BAND"];
var DPI_SOURCES = ["ASSUMED", "EMBEDDED_IF_PLAUSIBLE"];
var TRIM_MODES = ["ALPHA", "ALPHA_OR_BACKGROUND", "NONE"];
var PRESET_PRICING = ["STANDARD", "VARIANT_BAND_ONLY"];
var sizePresetSchema = z.object({
  id: z.string().regex(PRODUCT_OPTION_CHOICE_KEY_PATTERN),
  label: z.string().min(1).max(80),
  widthIn: z.number().positive().max(1e3),
  pricing: z.enum(PRESET_PRICING).default("STANDARD"),
  active: z.boolean().default(true)
});
var uploadConfigSchema = z.object({
  accept: z.object({
    mimeTypes: z.array(z.string().min(1).max(100)).max(50),
    maxFileMb: z.number().positive().max(2e3),
    maxFiles: z.number().int().min(1).max(50)
  }),
  sources: z.array(z.enum(UPLOAD_SOURCES)).min(1),
  gallery: z.object({
    categoryIds: z.array(z.string()).max(100).optional(),
    tagSlugs: z.array(z.string()).max(100).optional()
  }).optional(),
  imageTools: z.object({
    bgRemoval: z.enum(["OFF", "SUGGEST", "AUTO"]).default("OFF"),
    upscale: z.enum(["OFF", "SUGGEST"]).default("OFF"),
    /** Rotate, flip and crop on the working copy. */
    editor: z.enum(["OFF", "ON"]).default("OFF")
  }).optional(),
  sizing: z.object({
    assumedDpi: z.number().int().min(36).max(1200).default(300),
    /** False forces trim off, even when trimMode is set. */
    trim: z.boolean().default(true),
    minDpiWarn: z.number().int().min(1).max(1200).default(150),
    minSizeIn: z.number().positive().max(1e3).default(0.1),
    maxWidthIn: z.number().positive().max(1e3).default(22),
    maxHeightIn: z.number().positive().max(1e3).default(240),
    stepIn: z.number().positive().max(10).default(0.1),
    dpiSource: z.enum(DPI_SOURCES).default("ASSUMED"),
    /** At or above this, the DPI chip is print-ready. */
    dpiGood: z.number().int().min(1).max(1200).default(250),
    /** At or above this (and below dpiGood), the chip warns. */
    dpiFair: z.number().int().min(1).max(1200).default(150),
    trimMode: z.enum(TRIM_MODES).default("ALPHA"),
    trimAlphaThreshold: z.number().int().min(0).max(255).default(8),
    /** Customer may change the detected size. Gang sheets turn this off. */
    allowResize: z.boolean().default(true),
    /** Offer the post-trim size as a selectable size. */
    allowDetectedSize: z.boolean().default(true),
    /**
     * Customer may unlock the proportions and set width and height
     * independently (the artwork is stretched to that size). Only the
     * min/max bounds apply while unlocked. Gang sheets keep this off.
     */
    allowStretch: z.boolean().default(true),
    presets: z.array(sizePresetSchema).max(50).default([])
  }).optional(),
  /**
   * How the chosen size is priced. Absent means the variant price (and any
   * existing custom-size area formula) is unchanged.
   */
  sizePricing: z.object({
    mode: z.enum(SIZE_PRICE_MODES).default("AREA_RATE"),
    /** Blank whose price is the per-square-inch or per-inch rate. */
    pricingVariantId: z.string().min(1).max(200).nullable().optional()
  }).optional(),
  preview: z.object({
    mode: z.enum(["NONE", "IMAGE_ONLY", "IMAGE_ON_BG", "MASK_MOCKUP"]).default("NONE"),
    backgroundColorHex: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
    allowCustomerBgColor: z.boolean().default(false),
    printAreaId: z.string().nullable().optional(),
    bindBgColorToOptionTypeId: z.string().nullable().optional()
  }).optional(),
  variantBinding: z.object({
    /** Empty until the admin binds a variant axis. Readiness requires it for band pricing. */
    optionTypeId: z.string().max(200).default(""),
    defaultVariantRule: z.enum([
      "LONGEST_EDGE_CEIL",
      "HEIGHT_CEIL",
      "WIDTH_CEIL",
      "AREA_CEIL",
      "BOTH_EDGES_CEIL"
    ]).default("LONGEST_EDGE_CEIL"),
    customerCanChangeVariant: z.boolean().default(true),
    allowSmallerThanDetected: z.boolean().default(false),
    fixedWidthIn: z.number().positive().max(1e3).nullable().optional(),
    customSizeVariantId: z.string().nullable().optional()
  }).optional(),
  printRender: z.object({
    /** Always true: artwork is never distorted. */
    ratioLocked: z.literal(true).default(true),
    fit: z.enum(["CONTAIN"]).default("CONTAIN"),
    allowUpscale: z.boolean().default(true),
    minEffectiveDpiWarn: z.number().int().min(1).max(1200).default(150),
    minEffectiveDpiBlock: z.number().int().min(1).max(1200).nullable().optional()
  }).optional(),
  /** Gang sheet width check. Absent on DTF-by-size uploads. */
  gangsheet: z.object({
    /** Detected width may exceed the sheet maximum by this many inches. */
    widthToleranceIn: z.number().min(0).max(10).default(0.25)
  }).optional()
}).superRefine((config, ctx) => {
  const presets = config.sizing?.presets ?? [];
  const seen = /* @__PURE__ */ new Set();
  const maxWidth = config.sizing?.maxWidthIn;
  presets.forEach((preset, index) => {
    if (seen.has(preset.id)) {
      ctx.addIssue({
        code: "custom",
        path: ["sizing", "presets", index, "id"],
        message: `Preset id "${preset.id}" is already used.`
      });
    }
    seen.add(preset.id);
    if (maxWidth != null && preset.widthIn > maxWidth) {
      ctx.addIssue({
        code: "custom",
        path: ["sizing", "presets", index, "widthIn"],
        message: `Preset width must be at most ${maxWidth} in.`
      });
    }
  });
});
var optionUploadDraftSchema = z.object({
  schemaVersion: z.literal(1).default(1),
  widthIn: z.number().positive().max(1e3).optional(),
  heightIn: z.number().positive().max(1e3).optional(),
  /** `detected`, `custom`, or a preset id. */
  presetId: z.string().max(64).nullable().optional(),
  quantity: z.number().int().min(1).max(1e4).default(1),
  bgColorHex: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  /** Closed lock keeps the trimmed ratio. An open lock is bounds only. */
  ratioLocked: z.boolean().default(true)
}).superRefine((draft, ctx) => {
  if (draft.widthIn != null !== (draft.heightIn != null)) {
    ctx.addIssue({
      code: "custom",
      path: [draft.widthIn == null ? "widthIn" : "heightIn"],
      message: "Width and height are saved together."
    });
  }
});
var TEXT_TRANSFORMS = ["NONE", "UPPERCASE", "LOWERCASE", "CAPITALIZE"];
var textConfigSchema = z.object({
  minLength: z.number().int().min(0).max(1e5).default(0),
  maxLength: z.number().int().min(1).max(1e5).default(500),
  /** Customer input is validated against this; stored definitions are admin-authored. */
  pattern: z.string().max(500).nullable().optional(),
  showCounter: z.boolean().default(false),
  /** Applied to the stored value before length and pattern checks. */
  textTransform: z.enum(TEXT_TRANSFORMS).default("NONE")
});
var numberConfigSchema = z.object({
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
  step: z.number().positive().default(1)
});
var isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var DATE_DISPLAY_FORMATS = ["LOCALE", "YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY"];
var dateConfigSchema = z.object({
  minDate: isoDateSchema.nullable().optional(),
  maxDate: isoDateSchema.nullable().optional(),
  /** ISO dates (YYYY-MM-DD) the customer cannot pick. */
  blackoutDates: z.array(isoDateSchema).max(400).default([]),
  /** How the chosen ISO date is shown. Storage stays YYYY-MM-DD. */
  displayFormat: z.enum(DATE_DISPLAY_FORMATS).default("LOCALE")
});
var checkboxConfigSchema = z.object({
  label: z.string().max(200).optional()
});
var SWATCH_SIZES = ["SMALL", "MEDIUM", "LARGE"];
var SWATCH_SIZE_PX = {
  SMALL: 40,
  MEDIUM: 64,
  LARGE: 96
};
var swatchConfigSchema = z.object({
  swatchSize: z.enum(SWATCH_SIZES).default("SMALL")
});
function swatchSizePx(config) {
  const source = config && typeof config === "object" ? config : {};
  const parsed = swatchConfigSchema.safeParse(source);
  return SWATCH_SIZE_PX[parsed.success ? parsed.data.swatchSize : "SMALL"];
}
var checkboxGroupConfigSchema = z.object({
  minSelections: z.number().int().min(0).max(500).default(0),
  maxSelections: z.number().int().min(1).max(500).nullable().optional()
}).superRefine((value, ctx) => {
  if (value.maxSelections != null && value.maxSelections < value.minSelections) {
    ctx.addIssue({
      code: "custom",
      message: "Maximum selections must be at least the minimum.",
      path: ["maxSelections"]
    });
  }
});
var infoConfigSchema = z.object({
  /** Plain text only in v1; rendered as text content, never as HTML. */
  body: z.string().max(5e3)
});
var customSizeAxisSchema = z.number().positive().max(1e4);
var customSizeAxisGroupSchema = z.object({
  x: customSizeAxisSchema,
  y: customSizeAxisSchema,
  z: customSizeAxisSchema.optional()
});
var customSizeConfigSchema = z.preprocess(
  (input) => {
    if (!input || typeof input !== "object" || Array.isArray(input)) return input;
    const raw = input;
    if ("min" in raw || "unit" in raw || "dimensions" in raw) return input;
    if (!("minWidthIn" in raw) && !("maxWidthIn" in raw)) return input;
    const step = typeof raw.stepIn === "number" ? raw.stepIn : 0.5;
    return {
      unit: "in",
      dimensions: 2,
      labels: { x: "Width", y: "Height" },
      min: { x: raw.minWidthIn, y: raw.minHeightIn },
      max: { x: raw.maxWidthIn, y: raw.maxHeightIn },
      step: { x: step, y: step },
      defaults: {}
    };
  },
  z.object({
    unit: z.enum(["in", "cm"]).default("in"),
    dimensions: z.union([z.literal(2), z.literal(3)]).default(2),
    labels: z.object({
      x: z.string().min(1).max(40).default("Width"),
      y: z.string().min(1).max(40).default("Height"),
      z: z.string().min(1).max(40).optional()
    }).default({ x: "Width", y: "Height" }),
    min: customSizeAxisGroupSchema,
    max: customSizeAxisGroupSchema,
    step: customSizeAxisGroupSchema,
    defaults: z.object({
      x: customSizeAxisSchema.nullable().optional(),
      y: customSizeAxisSchema.nullable().optional(),
      z: customSizeAxisSchema.nullable().optional()
    }).default({}),
    /**
     * Blank variant whose surface price is the formula rate.
     * Absent means the dimension is stored and not priced.
     */
    pricingVariantId: z.string().min(1).max(64).nullable().optional()
  }).superRefine((value, ctx) => {
    const axes = value.dimensions === 3 ? ["x", "y", "z"] : ["x", "y"];
    for (const axis of axes) {
      const min = value.min[axis];
      const max = value.max[axis];
      const step = value.step[axis];
      if (min == null || max == null || step == null) {
        ctx.addIssue({
          code: "custom",
          message: `Set a minimum, maximum, and step for the ${axis} axis.`,
          path: ["min", axis]
        });
        continue;
      }
      if (max < min) {
        ctx.addIssue({
          code: "custom",
          message: "Maximum must be at least the minimum.",
          path: ["max", axis]
        });
      }
    }
  })
);
var displayRuleConditionSchema = z.object({
  source: z.enum(["OPTION", "VARIANT_AXIS", "QUANTITY"]),
  /** Option `key` or OptionType id, depending on source. */
  sourceKey: z.string().min(1).max(200),
  operator: z.enum(["EQ", "NEQ", "IN", "NOT_IN", "GTE", "LTE"]),
  /** Choice keys / axis value keys, or a single numeric quantity. */
  values: z.array(z.string().max(200)).max(100)
});
var displayRuleSchema = z.object({
  action: z.enum(["SHOW", "HIDE"]),
  combinator: z.enum(["AND", "OR"]).default("AND"),
  conditions: z.array(displayRuleConditionSchema).min(1).max(20)
});
var displayRulesSchema = z.array(displayRuleSchema).max(20);
var optionSelectionSchema = z.object({
  optionKey: z.string().min(1).max(64),
  /** Choice keys for choice types; raw text/number/date otherwise. */
  values: z.array(z.string().max(1e5)).max(50),
  /** OptionUpload public tokens referenced by upload types. */
  uploadTokens: z.array(z.string().max(64)).max(50).optional()
});
var variantAxisSelectionSchema = z.object({
  optionTypeId: z.string().min(1).max(200),
  valueKey: z.string().min(1).max(200)
});
var optionSubmissionPayloadSchema = z.object({
  schemaVersion: z.literal(PRODUCT_OPTIONS_SCHEMA_VERSION),
  uptId: z.string().min(1),
  /** Shopify variant id or internal variant id, depending on surface. */
  variantRef: z.string().min(1).nullable(),
  quantity: z.number().int().min(1).max(1e4),
  selections: z.array(optionSelectionSchema).max(100),
  variantAxes: z.array(variantAxisSelectionSchema).max(20).optional()
});
var quantityTierSchema = z.object({
  minQty: z.number().int().min(1).max(1e4),
  discountPercent: z.number().min(0).max(100)
});
var SINGLE_QUANTITY = { mode: "SINGLE", min: 1, max: 100, step: 1, tiers: [] };
var quantityConfigSchema = z.preprocess(
  (input) => {
    if (!input || typeof input !== "object" || Array.isArray(input)) return SINGLE_QUANTITY;
    const raw = input;
    return { ...raw, mode: raw.mode === "BULK" ? "BULK" : "SINGLE" };
  },
  z.object({
    mode: z.enum(["SINGLE", "BULK"]).default("SINGLE"),
    min: z.number().int().min(1).max(1e4).default(1),
    max: z.number().int().min(1).max(1e4).default(100),
    step: z.number().int().min(1).max(1e3).default(1),
    tiers: z.array(quantityTierSchema).max(20).default([])
  }).superRefine((value, ctx) => {
    if (value.max < value.min) {
      ctx.addIssue({
        code: "custom",
        message: "Quantity max must be at least the minimum.",
        path: ["max"]
      });
    }
    if (value.mode === "SINGLE") {
      if (value.tiers.length > 0) {
        ctx.addIssue({
          code: "custom",
          message: "Single quantity does not use bulk tiers.",
          path: ["tiers"]
        });
      }
      return;
    }
    if (value.tiers.length === 0) {
      ctx.addIssue({
        code: "custom",
        message: "Add at least one bulk tier.",
        path: ["tiers"]
      });
      return;
    }
    let previous = 0;
    value.tiers.forEach((tier, index) => {
      if (index === 0 && tier.minQty !== value.min) {
        ctx.addIssue({
          code: "custom",
          message: "The first tier starts at the block minimum.",
          path: ["tiers", index, "minQty"]
        });
      }
      if (tier.minQty <= previous) {
        ctx.addIssue({
          code: "custom",
          message: "Tiers must be unique and sorted by quantity.",
          path: ["tiers", index, "minQty"]
        });
      }
      previous = tier.minQty;
      if (tier.minQty < value.min || tier.minQty > value.max) {
        ctx.addIssue({
          code: "custom",
          message: "Tier quantity must sit inside the block minimum and maximum.",
          path: ["tiers", index, "minQty"]
        });
      }
      if (value.step > 0 && (tier.minQty - value.min) % value.step !== 0) {
        ctx.addIssue({
          code: "custom",
          message: "Tier quantity must land on the step grid.",
          path: ["tiers", index, "minQty"]
        });
      }
    });
  })
);
var optionsBlockConfigSchema = z.object({
  schemaVersion: z.literal(PRODUCT_OPTIONS_SCHEMA_VERSION).default(PRODUCT_OPTIONS_SCHEMA_VERSION),
  quantity: quantityConfigSchema.default(SINGLE_QUANTITY),
  /**
   * Per-piece floor in shop-currency cents for sized products. Applied to
   * the size price before option charges and tiers. 0 means no floor.
   */
  minChargeCents: z.number().int().min(0).max(1e7).default(0),
  hideNativeSelectors: z.boolean().default(true),
  nativeSelectorCss: z.string().max(2e3).default(""),
  showPrice: z.boolean().default(true),
  addToCartLabel: z.string().min(1).max(80).default("Add to cart"),
  uploadPanel: z.object({
    sources: z.array(z.enum(UPLOAD_SOURCES)).min(1).max(5).default(["DEVICE", "RECENT"])
  }).default({ sources: ["DEVICE", "RECENT"] })
}).superRefine((value, ctx) => {
  if (value.quantity.max < value.quantity.min) {
    ctx.addIssue({
      code: "custom",
      message: "Quantity max must be at least the minimum.",
      path: ["quantity", "max"]
    });
  }
});
var VARIANT_MATCH_RULES = [
  "LONGEST_EDGE_CEIL",
  "HEIGHT_CEIL",
  "WIDTH_CEIL",
  "AREA_CEIL",
  "BOTH_EDGES_CEIL"
];
var OPTION_SUBMISSION_TOKEN_PATTERN = /^OPS_[A-Za-z0-9]{20,}$/;
var OPTION_UPLOAD_TOKEN_PATTERN = /^OU_[A-Za-z0-9]{20,}$/;

// src/product-options/choices.ts
var SINGLE_DEFAULT_TYPES = /* @__PURE__ */ new Set([
  "RADIO",
  "SELECT",
  "SWATCH",
  "BUTTON_GROUP"
]);
function isSingleSelectOptionType(optionType) {
  return SINGLE_DEFAULT_TYPES.has(optionType);
}
function blankToNull(value) {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}
function normalizeChoices(optionType, choices) {
  const singleDefault = isSingleSelectOptionType(optionType);
  let defaultKept = false;
  return choices.map((choice, index) => {
    const imageUrl = blankToNull(choice.imageUrl);
    let isDefault = choice.isDefault;
    if (singleDefault) {
      if (isDefault && defaultKept) isDefault = false;
      else if (isDefault) defaultKept = true;
    }
    return {
      ...choice,
      imageUrl,
      iconUrl: blankToNull(choice.iconUrl),
      colorHex: imageUrl ? null : choice.colorHex ?? null,
      isDefault,
      sortOrder: index
    };
  });
}

// src/product-options/size-math.ts
var SIZE_DISPLAY_DECIMALS = 2;
function roundDisplay(inches) {
  if (!Number.isFinite(inches)) return 0;
  const factor = 10 ** SIZE_DISPLAY_DECIMALS;
  return Math.round(inches * factor) / factor;
}
function formatInches(inches) {
  return String(roundDisplay(inches));
}
function physicalInches(px, dpi) {
  if (!Number.isFinite(px) || !Number.isFinite(dpi) || dpi <= 0 || px < 0) return 0;
  return px / dpi;
}
function resolveEffectiveDpi(input) {
  const assumed = input.assumedDpi > 0 ? input.assumedDpi : 300;
  if (input.dpiSource !== "EMBEDDED_IF_PLAUSIBLE") return assumed;
  const embedded = input.embeddedDpi;
  if (embedded == null || !Number.isFinite(embedded)) return assumed;
  if (embedded < 36 || embedded > 1200) return assumed;
  return embedded;
}
function detectedSizeIn(input) {
  return {
    widthIn: physicalInches(input.trimmed.widthPx, input.dpi),
    heightIn: physicalInches(input.trimmed.heightPx, input.dpi)
  };
}
function heightFromWidth(widthIn, trimmed) {
  if (trimmed.widthPx <= 0 || trimmed.heightPx <= 0) return widthIn;
  return widthIn * (trimmed.heightPx / trimmed.widthPx);
}
function widthFromHeight(heightIn, trimmed) {
  if (trimmed.widthPx <= 0 || trimmed.heightPx <= 0) return heightIn;
  return heightIn * (trimmed.widthPx / trimmed.heightPx);
}
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
function fitSize(input) {
  const ratio = input.ratio > 0 && Number.isFinite(input.ratio) ? input.ratio : 1;
  const min = input.minSizeIn;
  const maxW = input.maxWidthIn;
  const maxH = input.maxHeightIn;
  let width = Number.isFinite(input.widthIn) && input.widthIn > 0 ? input.widthIn : min;
  let height = Number.isFinite(input.heightIn) && input.heightIn > 0 ? input.heightIn : min;
  const beforeW = width;
  const beforeH = height;
  if (input.ratioLocked) {
    if (input.edited === "height") {
      height = clamp(height, min, maxH);
      width = height * ratio;
    } else {
      width = clamp(width, min, maxW);
      height = width / ratio;
    }
    if (width > maxW) {
      width = maxW;
      height = width / ratio;
    }
    if (height > maxH) {
      height = maxH;
      width = height * ratio;
    }
    if (width < min) {
      width = min;
      height = width / ratio;
    }
    if (height < min) {
      height = min;
      width = height * ratio;
    }
    if (width > maxW) {
      width = maxW;
      height = width / ratio;
    }
  } else {
    width = clamp(width, min, maxW);
    height = clamp(height, min, maxH);
  }
  return {
    widthIn: width,
    heightIn: height,
    clamped: width !== beforeW || height !== beforeH
  };
}
function effectivePrintDpi(trimmedPx, inches) {
  if (!Number.isFinite(trimmedPx) || !Number.isFinite(inches) || inches <= 0) return 0;
  return trimmedPx / inches;
}
function dpiQuality(dpi, good, fair) {
  if (dpi >= good) return "good";
  if (dpi >= fair) return "fair";
  return "low";
}
function gangsheetVariantHeight(widthIn, heightIn) {
  if (typeof heightIn === "number" && Number.isFinite(heightIn) && heightIn > 0) return heightIn;
  if (typeof widthIn === "number" && Number.isFinite(widthIn) && widthIn > 0) return widthIn;
  return null;
}
function gangsheetFixedWidthIn(fixedWidthIn) {
  return typeof fixedWidthIn === "number" && Number.isFinite(fixedWidthIn) && fixedWidthIn > 0 ? fixedWidthIn : 22;
}
function sizeFormulaMode(mode) {
  switch (mode) {
    case "AREA_RATE":
      return "AREA";
    case "LONGEST_EDGE_RATE":
      return "LONGEST_EDGE";
    case "HEIGHT_RATE":
      return "HEIGHT";
    case "VARIANT_BAND":
      return null;
    default: {
      const unreachable = mode;
      return unreachable;
    }
  }
}
function sizeRateInches(mode, widthIn, heightIn) {
  switch (mode) {
    case "AREA":
      return widthIn * heightIn;
    case "LONGEST_EDGE":
      return Math.max(widthIn, heightIn);
    case "HEIGHT":
      return heightIn;
    default: {
      const unreachable = mode;
      return unreachable;
    }
  }
}
function priceForSize(input) {
  const formulaMode = sizeFormulaMode(input.mode);
  if (formulaMode == null) return null;
  if (!Number.isFinite(input.widthIn) || !Number.isFinite(input.heightIn) || !Number.isFinite(input.rateCents) || input.widthIn <= 0 || input.heightIn <= 0) {
    return null;
  }
  const raw = Math.round(sizeRateInches(formulaMode, input.widthIn, input.heightIn) * input.rateCents);
  const floor = input.minChargeCents != null && Number.isFinite(input.minChargeCents) ? Math.max(0, Math.round(input.minChargeCents)) : 0;
  return Math.max(raw, floor);
}
var SIZED_STRETCH_FLAG = "stretch";
function isStretchedSelection(values) {
  return values[3]?.trim() === SIZED_STRETCH_FLAG;
}
function validateSizedValues(input) {
  if (input.required && input.uploadCount === 0) return "Upload an image.";
  if (input.uploadCount > input.maxFiles) return `Up to ${input.maxFiles} files.`;
  if (input.uploadCount === 0) return null;
  if (input.values.length < 2) return "Choose a print size.";
  if (input.values.length > 4) return "Enter a valid print size.";
  const width = Number(input.values[0]);
  const height = Number(input.values[1]);
  const preset = input.values[2]?.trim() || "custom";
  const stretched = isStretchedSelection(input.values);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return "Enter a valid print size.";
  }
  if (input.values[3] != null && input.values[3].trim() !== "" && !stretched) {
    return "Enter a valid print size.";
  }
  if (stretched && (!input.allowStretch || input.fixedWidthIn != null)) {
    return "This product keeps the image proportions.";
  }
  if (input.fixedWidthIn != null && width > input.fixedWidthIn + 1e-3) {
    return `Gang sheet width cannot exceed ${input.fixedWidthIn} in.`;
  }
  if (!input.allowResize && (preset !== "detected" || stretched)) {
    return "This product uses the detected size.";
  }
  const { minSizeIn, maxWidthIn, maxHeightIn } = input.bounds;
  if (width < minSizeIn || width > maxWidthIn) {
    return `Width must be between ${minSizeIn} and ${maxWidthIn} in.`;
  }
  if (height < minSizeIn || height > maxHeightIn) {
    return `Height must be between ${minSizeIn} and ${maxHeightIn} in.`;
  }
  return null;
}
function heightMatchesTrimmedRatio(widthIn, heightIn, trimmed) {
  if (trimmed.widthPx <= 0 || trimmed.heightPx <= 0) return false;
  const expected = heightFromWidth(widthIn, trimmed);
  if (expected <= 0) return false;
  const delta = Math.abs(heightIn - expected);
  return delta / expected <= 5e-3 || delta <= 5e-3;
}
function sizingBounds(config) {
  const sizing = config.sizing;
  return {
    minSizeIn: sizing?.minSizeIn ?? 0.1,
    maxWidthIn: sizing?.maxWidthIn ?? 22,
    maxHeightIn: sizing?.maxHeightIn ?? 240,
    stepIn: sizing?.stepIn ?? 0.1
  };
}
function sizedCheckoutDimension(input) {
  const min = input.gangSheet ? Math.min(input.minSizeIn, 0.01) : input.minSizeIn;
  const step = 1e-6;
  return {
    x: { min, max: input.maxWidthIn, step },
    y: { min, max: input.maxHeightIn, step }
  };
}
function measuredPrintInches(input) {
  if (input.trimmedWidthPx < 1 || input.trimmedHeightPx < 1) return null;
  if (!(input.nativeWidthIn > 0) || !(input.nativeHeightIn > 0)) return null;
  const cap = input.sheetMaxWidthIn;
  const bounds = cap != null && cap > 0 ? {
    ...input.bounds,
    maxWidthIn: Math.min(input.bounds.maxWidthIn, cap),
    minSizeIn: Math.min(input.bounds.minSizeIn, 0.01)
  } : input.bounds;
  const fitted = fitSize({
    ...bounds,
    widthIn: input.nativeWidthIn,
    heightIn: input.nativeHeightIn,
    ratio: input.trimmedWidthPx / input.trimmedHeightPx,
    ratioLocked: true,
    edited: "width"
  });
  if (!(fitted.widthIn > 0) || !(fitted.heightIn > 0)) return null;
  return { widthIn: roundDisplay(fitted.widthIn), heightIn: roundDisplay(fitted.heightIn) };
}
function uploadProgressPercent(stage, uploadRatio) {
  const ratio = Number.isFinite(uploadRatio) ? Math.min(1, Math.max(0, uploadRatio)) : 0;
  switch (stage) {
    case "queued":
      return 0;
    case "uploading":
      return Math.round(ratio * 85);
    case "scanning":
      return 88;
    case "detecting":
      return 94;
    case "preview":
      return 98;
    case "ready":
      return 100;
    case "failed":
      return Math.round(ratio * 85);
    default: {
      const unreachable = stage;
      return unreachable;
    }
  }
}
function monotonicProgress(previous, next) {
  if (!Number.isFinite(previous)) return next;
  return Math.max(previous, next);
}
function pixelsPerMeterForInches(px, inches) {
  if (!Number.isFinite(px) || !Number.isFinite(inches) || px <= 0 || inches <= 0) return 0;
  const meters = inches * 0.0254;
  return Math.max(1, Math.round(px / meters));
}

// src/product-options/display-value.ts
function applyTextTransform(value, transform) {
  switch (transform) {
    case "UPPERCASE":
      return value.toLocaleUpperCase("en-US");
    case "LOWERCASE":
      return value.toLocaleLowerCase("en-US");
    case "CAPITALIZE":
      return value.replace(/\S+/g, (word) => {
        const [first, ...rest] = [...word];
        if (!first) return word;
        return first.toLocaleUpperCase("en-US") + rest.join("").toLocaleLowerCase("en-US");
      });
    default:
      return value;
  }
}
function formatIsoDate(iso, format) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const [year, month, day] = iso.split("-");
  switch (format) {
    case "YYYY-MM-DD":
      return iso;
    case "DD/MM/YYYY":
      return `${day}/${month}/${year}`;
    case "MM/DD/YYYY":
      return `${month}/${day}/${year}`;
    default: {
      const date = /* @__PURE__ */ new Date(`${iso}T00:00:00Z`);
      if (Number.isNaN(date.getTime())) return iso;
      return new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeZone: "UTC"
      }).format(date);
    }
  }
}
function optionDisplayValue(input) {
  const { optionType, values } = input;
  if (optionType === "CHECKBOX" || optionType === "BOOLEAN") {
    return values[0] === "true" ? "Yes" : "No";
  }
  if ((input.uploadTokens?.length ?? 0) > 0 && values.every((value) => value.trim() === "")) {
    return "";
  }
  if (optionType === "DATE") {
    const raw = values[0]?.trim() ?? "";
    if (raw === "") return "";
    const parsed = dateConfigSchema.safeParse(input.config ?? {});
    const format = parsed.success ? parsed.data.displayFormat : "LOCALE";
    return formatIsoDate(raw, format);
  }
  if (optionType === "IMAGE_UPLOAD_SIZED" || optionType === "GANGSHEET_UPLOAD_SIZED") {
    return formatSizedUploadValue(values, input.config);
  }
  if (optionType === "CUSTOM_SIZE") {
    const parsed = customSizeConfigSchema.safeParse(input.config ?? {});
    const unit = parsed.success ? parsed.data.unit : "in";
    const shown = values.map((value) => value.trim()).filter((value) => value !== "");
    return shown.length === 0 ? "" : `${shown.join(" \xD7 ")} ${unit}`;
  }
  if (optionType === "TEXT" || optionType === "TEXTAREA" || optionType === "STRING") {
    const raw = values[0]?.trim() ?? "";
    if (raw === "") return "";
    const parsed = textConfigSchema.safeParse(input.config ?? {});
    const transform = parsed.success ? parsed.data.textTransform : "NONE";
    return applyTextTransform(raw, transform);
  }
  const labels = new Map((input.choices ?? []).map((choice) => [choice.key, choice.label]));
  return values.map((value) => labels.get(value) ?? value).filter((value) => value.trim() !== "").join(", ");
}
function formatSizedUploadValue(values, config) {
  const width = Number(values[0]);
  const height = Number(values[1]);
  if (!Number.isFinite(width) || !Number.isFinite(height)) return "";
  const stretched = values[3]?.trim() === "stretch" ? " (stretched)" : "";
  const size = `${formatInches(width)} \xD7 ${formatInches(height)} in${stretched}`;
  const presetId = (values[2] ?? "").trim();
  const label = presetLabel(config, presetId);
  if (label) return `${label}: ${size}`;
  if (presetId === "custom") return `Custom: ${size}`;
  if (presetId === "detected" || presetId === "") return `Detected: ${size}`;
  return size;
}
function presetLabel(config, presetId) {
  if (!presetId || !config || typeof config !== "object") return null;
  const sizing = config.sizing;
  if (!sizing || !Array.isArray(sizing.presets)) return null;
  for (const item of sizing.presets) {
    if (!item || typeof item !== "object") continue;
    const row = item;
    if (row.id === presetId && typeof row.label === "string" && row.label.trim()) {
      return row.label.trim();
    }
  }
  return null;
}

// src/product-options/defaults.ts
var CHOICE_TYPES = /* @__PURE__ */ new Set([
  "RADIO",
  "SELECT",
  "SWATCH",
  "BUTTON_GROUP",
  "CHECKBOX_GROUP"
]);
function initialSelection(option) {
  if (CHOICE_TYPES.has(option.optionType)) {
    const parsed = productOptionChoicesSchema.safeParse(option.values ?? []);
    if (!parsed.success) return [];
    const keys = parsed.data.filter((choice) => choice.isDefault).map((choice) => choice.key);
    return isSingleSelectOptionType(option.optionType) ? keys.slice(0, 1) : keys;
  }
  if (option.optionType === "CHECKBOX" || option.optionType === "BOOLEAN") {
    return option.defaultValue === "true" ? ["true"] : [];
  }
  if (option.optionType === "CUSTOM_SIZE") {
    const parsed = customSizeConfigSchema.safeParse(option.config ?? {});
    if (!parsed.success) return [];
    const defaults = parsed.data.defaults;
    if (defaults.x == null || defaults.y == null) return [];
    const selected = [String(defaults.x), String(defaults.y)];
    if (parsed.data.dimensions === 3) {
      if (defaults.z == null) return [];
      selected.push(String(defaults.z));
    }
    return selected;
  }
  const raw = option.defaultValue?.trim() ?? "";
  if (raw === "") return [];
  if (option.optionType === "TEXT" || option.optionType === "TEXTAREA" || option.optionType === "STRING") {
    const parsed = textConfigSchema.safeParse(option.config ?? {});
    const transform = parsed.success ? parsed.data.textTransform : "NONE";
    return [applyTextTransform(raw, transform)];
  }
  return [raw];
}

// src/product-options/eligibility.ts
var PRODUCT_OPTIONS_ELIGIBLE_DESIGNER_MODE = "NONE";
function isProductOptionsEligible(template) {
  return template.designerMode === PRODUCT_OPTIONS_ELIGIBLE_DESIGNER_MODE;
}
var SIZED_UPLOAD_PRODUCT_TYPES = {
  IMAGE_UPLOAD_SIZED: ["DTF_BY_SIZE"],
  GANGSHEET_UPLOAD_SIZED: ["DTF_BY_SIZE", "GANGSHEET"]
};
function isSizedUploadOptionType(optionType) {
  return Object.prototype.hasOwnProperty.call(SIZED_UPLOAD_PRODUCT_TYPES, optionType);
}
function sizedUploadEligibilityError(optionType, template) {
  const allowed = SIZED_UPLOAD_PRODUCT_TYPES[optionType];
  if (!allowed) return null;
  if (template.designerMode !== PRODUCT_OPTIONS_ELIGIBLE_DESIGNER_MODE) {
    return "Sized uploads are available only when the designer mode is None.";
  }
  if (!allowed.includes(template.type)) {
    return optionType === "GANGSHEET_UPLOAD_SIZED" ? "Gang sheet upload is available on DTF by Size and Gangsheet products only." : "DTF by Size upload is available on DTF by Size products only.";
  }
  return null;
}

// src/product-options/display-rules.ts
function conditionMatches(condition, context) {
  const actuals = condition.source === "OPTION" ? context.selections[condition.sourceKey] ?? [] : condition.source === "VARIANT_AXIS" ? context.variantAxes[condition.sourceKey] !== void 0 ? [context.variantAxes[condition.sourceKey]] : [] : [String(context.quantity)];
  const expected = condition.values;
  switch (condition.operator) {
    case "EQ":
      return actuals.length > 0 && actuals.every((v) => expected.includes(v));
    case "NEQ":
      return actuals.every((v) => !expected.includes(v));
    case "IN":
      return actuals.some((v) => expected.includes(v));
    case "NOT_IN":
      return actuals.every((v) => !expected.includes(v));
    case "GTE":
    case "LTE": {
      const threshold = Number(expected[0]);
      const value = Number(actuals[0]);
      if (!Number.isFinite(threshold) || !Number.isFinite(value)) return false;
      return condition.operator === "GTE" ? value >= threshold : value <= threshold;
    }
    default:
      return false;
  }
}
function ruleMatches(rule, context) {
  const results = rule.conditions.map((c) => conditionMatches(c, context));
  return rule.combinator === "OR" ? results.some(Boolean) : results.every(Boolean);
}
function isOptionVisible(rules, context) {
  const hasShow = rules.some((rule) => rule.action === "SHOW");
  for (const rule of rules) {
    if (ruleMatches(rule, context)) return rule.action === "SHOW";
  }
  return !hasShow;
}
function visibleInOrder(options, context, rulesFor) {
  const selections = {};
  const shown = [];
  for (const option of options) {
    const rules = rulesFor(option);
    const visible = rules == null ? true : isOptionVisible(rules, {
      selections,
      variantAxes: context.variantAxes,
      quantity: context.quantity
    });
    if (!visible) continue;
    shown.push(option);
    const selected = context.selections[option.key];
    if (selected?.some((value) => value.trim() !== "")) selections[option.key] = selected;
  }
  return shown;
}

// src/product-options/pricing-resolver.ts
function activeQuantityTier(tiers, quantity) {
  let active = null;
  for (const tier of tiers) {
    if (tier.minQty <= quantity && (active == null || tier.minQty > active.minQty)) {
      active = tier;
    }
  }
  return active;
}
function deltaForSelection(option, variantUnitPriceCents, selectedChoiceKeys) {
  if (option.priceDeltaMode === "NONE") return null;
  if (option.priceDeltaMode === "PERCENT_OF_VARIANT") {
    const percent = option.priceDeltaPercent ?? 0;
    return {
      cents: Math.round(variantUnitPriceCents * percent / 100),
      mode: "PERCENT_OF_VARIANT"
    };
  }
  const keys = selectedChoiceKeys.filter((key) => key !== "");
  let cents;
  if (keys.length === 0) {
    cents = option.priceDeltaCents ?? 0;
  } else if (keys.length === 1) {
    const delta = option.choiceDeltas[keys[0]];
    cents = delta !== void 0 ? delta : option.priceDeltaCents ?? 0;
  } else {
    const priced = keys.filter((key) => option.choiceDeltas[key] !== void 0);
    cents = priced.length === 0 ? option.priceDeltaCents ?? 0 : priced.reduce((sum, key) => sum + (option.choiceDeltas[key] ?? 0), 0);
  }
  return { cents, mode: option.priceDeltaMode };
}
function formulaRawCents(formula) {
  if (!Number.isFinite(formula.width) || !Number.isFinite(formula.height) || !Number.isFinite(formula.rateCents) || formula.width <= 0 || formula.height <= 0) {
    return null;
  }
  const measure2 = sizeRateInches(formula.mode ?? "AREA", formula.width, formula.height);
  return Math.round(measure2 * formula.rateCents);
}
function resolveOptionPrice(input) {
  const quantity = Math.max(1, Math.trunc(input.quantity));
  const tierQuantity = input.tierQuantity != null && Number.isFinite(input.tierQuantity) ? Math.max(1, Math.trunc(input.tierQuantity)) : quantity;
  const formula = input.formula;
  const rawFormulaCents = formula ? formulaRawCents(formula) : null;
  const floor = formula?.minChargeCents != null && Number.isFinite(formula.minChargeCents) ? Math.max(0, Math.round(formula.minChargeCents)) : 0;
  const formulaUnitCents = rawFormulaCents == null ? null : Math.max(rawFormulaCents, floor);
  const percentBase = formulaUnitCents ?? input.variantUnitPriceCents;
  const surcharges = [];
  let exactSurchargeCents = 0;
  for (const option of input.options) {
    if (!Object.prototype.hasOwnProperty.call(input.selections, option.key)) continue;
    const selected = input.selections[option.key] ?? [];
    const delta = deltaForSelection(option, percentBase, selected);
    if (!delta || delta.cents === 0) continue;
    const amountCentsPerUnit = delta.mode === "PER_LINE" ? Math.trunc(delta.cents / quantity) : delta.cents;
    exactSurchargeCents += delta.mode === "PER_LINE" ? delta.cents : delta.cents * quantity;
    const choiceKey = selected.length === 1 && selected[0] !== "" ? selected[0] : null;
    surcharges.push({
      optionKey: option.key,
      choiceKey,
      label: option.label,
      mode: delta.mode,
      amountCentsPerUnit
    });
  }
  const surchargePerUnitCents = surcharges.reduce(
    (sum, line) => sum + line.amountCentsPerUnit,
    0
  );
  const tier = formulaUnitCents == null && (input.tiers == null || input.tiers.length === 0) ? null : activeQuantityTier(input.tiers ?? [], tierQuantity);
  const discountPercent = tier?.discountPercent ?? 0;
  const unitBeforeDiscount = (formulaUnitCents ?? input.variantUnitPriceCents) + surchargePerUnitCents;
  if (formulaUnitCents == null && discountPercent === 0) {
    return {
      variantUnitPriceCents: input.variantUnitPriceCents,
      surcharges,
      surchargePerUnitCents,
      quantity,
      formulaUnitCents: null,
      discountPercent: 0,
      discountCents: 0,
      customerUnitCents: input.variantUnitPriceCents + surchargePerUnitCents,
      lineTotalCents: input.variantUnitPriceCents * quantity + exactSurchargeCents
    };
  }
  const discountCents = Math.round(unitBeforeDiscount * discountPercent / 100);
  const customerUnitCents = unitBeforeDiscount - discountCents;
  return {
    variantUnitPriceCents: input.variantUnitPriceCents,
    surcharges,
    surchargePerUnitCents,
    quantity,
    formulaUnitCents,
    discountPercent,
    discountCents,
    customerUnitCents,
    lineTotalCents: customerUnitCents * quantity
  };
}

// src/product-options/checkout-pricing.ts
var DELTA_MODES = /* @__PURE__ */ new Set(["PER_UNIT", "PER_LINE", "PERCENT_OF_VARIANT"]);
var SHOPIFY_VARIANT_GID_PREFIX = "gid://shopify/ProductVariant/";
function shopifyVariantNumericId(value) {
  if (value == null) return null;
  const text = value.trim();
  if (/^\d+$/.test(text)) return text;
  if (text.startsWith(SHOPIFY_VARIANT_GID_PREFIX)) {
    const id = text.slice(SHOPIFY_VARIANT_GID_PREFIX.length);
    return /^\d+$/.test(id) ? id : null;
  }
  return null;
}
function buildCheckoutPricingPayload(input) {
  const deltas = [];
  for (const option of input.options) {
    if (!option.key || !DELTA_MODES.has(option.priceDeltaMode)) continue;
    const choices = {};
    for (const choice of option.choices) {
      if (typeof choice.priceDeltaCents === "number") choices[choice.key] = choice.priceDeltaCents;
    }
    const percent = typeof option.priceDeltaPercent === "number" && Number.isFinite(option.priceDeltaPercent) ? option.priceDeltaPercent : null;
    deltas.push({
      key: option.key,
      mode: option.priceDeltaMode,
      cents: option.priceDeltaCents ?? 0,
      percent,
      choices
    });
  }
  const dimension = input.options.find((option) => option.priced === true && option.dimension)?.dimension ?? null;
  const requiresOptions = input.options.some(
    (option) => option.priced === true || option.required === true
  );
  const minCharge = input.minChargeCents != null && Number.isFinite(input.minChargeCents) ? Math.max(0, Math.round(input.minChargeCents)) : 0;
  const productId = input.productId?.trim();
  return {
    schemaVersion: 1,
    ...productId ? { productId } : {},
    rateCents: input.rateCents != null && Number.isFinite(input.rateCents) ? Math.round(input.rateCents) : null,
    minChargeCents: minCharge,
    dimension,
    dimensionVariantId: shopifyVariantNumericId(input.dimensionVariantId),
    requiresOptions,
    quantity: {
      mode: input.quantity.mode,
      min: input.quantity.min,
      max: input.quantity.max,
      step: input.quantity.step,
      tiers: input.quantity.mode === "BULK" ? input.quantity.tiers.map((tier) => ({ ...tier })) : []
    },
    deltas,
    sizePricing: checkoutSizePricing(input.sizePricing)
  };
}
function checkoutSizePricing(pricing) {
  if (!pricing) return null;
  const fixed = pricing.fixedWidthIn;
  const bands = (pricing.bands ?? []).flatMap((band) => {
    if (!band.variantId || !Number.isFinite(band.heightIn) || band.heightIn <= 0) return [];
    return [
      {
        variantId: band.variantId,
        heightIn: band.heightIn,
        priceCents: Number.isFinite(band.priceCents) ? Math.round(band.priceCents) : 0
      }
    ];
  });
  return {
    mode: pricing.mode,
    tierAcrossLines: pricing.tierAcrossLines === true,
    ...typeof fixed === "number" && Number.isFinite(fixed) && fixed > 0 ? { fixedWidthIn: fixed } : {},
    ...bands.length > 0 ? { bands, allowSmaller: pricing.allowSmaller === true } : {}
  };
}
function encodeCheckoutSelections(selections) {
  return selections.filter((selection) => selection.key !== "").map(
    (selection) => selection.choices.length > 0 ? `${selection.key}:${selection.choices.join("+")}` : selection.key
  ).join("|");
}
function decodeCheckoutSelections(raw) {
  if (!raw) return [];
  const selections = [];
  for (const part of raw.split("|")) {
    if (part === "") continue;
    const splitAt = part.indexOf(":");
    const key = splitAt === -1 ? part : part.slice(0, splitAt);
    const choicePart = splitAt === -1 ? "" : part.slice(splitAt + 1);
    if (key === "") continue;
    selections.push({
      key,
      choices: choicePart === "" ? [] : choicePart.split("+").filter((choice) => choice !== "")
    });
  }
  return selections;
}
function recomputeCheckoutUnitCents(input) {
  if (!Number.isFinite(input.variantUnitCents) || input.variantUnitCents < 0) return null;
  const selected = new Map(input.selected.map((item) => [item.key, item.choices]));
  const options = input.payload.deltas.map((delta) => ({
    key: delta.key,
    label: delta.key,
    priceDeltaMode: delta.mode,
    priceDeltaCents: delta.cents,
    priceDeltaPercent: delta.percent,
    choiceDeltas: delta.choices
  }));
  const selections = {};
  for (const delta of input.payload.deltas) {
    const choices = selected.get(delta.key);
    if (choices) selections[delta.key] = choices;
  }
  const width = input.width;
  const height = input.height;
  const rate = input.payload.rateCents;
  const mode = input.payload.sizePricing?.mode ?? "AREA_RATE";
  const formula = mode === "VARIANT_BAND" || width == null || height == null || rate == null || !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0 ? null : {
    width,
    height,
    rateCents: rate,
    minChargeCents: input.payload.minChargeCents,
    mode: sizeFormulaMode(mode) ?? "AREA"
  };
  const resolved = resolveOptionPrice({
    variantUnitPriceCents: input.variantUnitCents,
    quantity: input.quantity,
    options,
    selections,
    formula,
    tiers: input.payload.quantity.mode === "BULK" ? input.payload.quantity.tiers : void 0,
    tierQuantity: input.tierQuantity
  });
  return resolved.customerUnitCents;
}
function checkoutQuantityError(quantity, quantityConfig) {
  if (!Number.isFinite(quantity) || quantity < quantityConfig.min || quantity > quantityConfig.max) {
    return `Quantity must be between ${quantityConfig.min} and ${quantityConfig.max}.`;
  }
  if ((quantity - quantityConfig.min) % quantityConfig.step !== 0) {
    return `Quantity must increase in steps of ${quantityConfig.step}.`;
  }
  return null;
}

// src/product-options/variant-match.ts
function measure(candidate, input) {
  switch (input.rule) {
    case "HEIGHT_CEIL":
      return candidate.heightIn;
    case "WIDTH_CEIL":
      return candidate.widthIn;
    case "AREA_CEIL":
      return candidate.widthIn * candidate.heightIn;
    case "LONGEST_EDGE_CEIL":
      return Math.max(candidate.widthIn, candidate.heightIn);
    case "BOTH_EDGES_CEIL":
      return 0;
  }
}
function target(input) {
  switch (input.rule) {
    case "HEIGHT_CEIL":
      return input.detectedHeightIn;
    case "WIDTH_CEIL":
      return input.detectedWidthIn;
    case "AREA_CEIL":
      return input.detectedWidthIn * input.detectedHeightIn;
    case "LONGEST_EDGE_CEIL":
      return Math.max(input.detectedWidthIn, input.detectedHeightIn);
    case "BOTH_EDGES_CEIL":
      return 0;
  }
}
function fitsBothEdges(candidate, input) {
  const fitsNormal = candidate.widthIn >= input.detectedWidthIn && candidate.heightIn >= input.detectedHeightIn;
  const fitsRotated = candidate.widthIn >= input.detectedHeightIn && candidate.heightIn >= input.detectedWidthIn;
  return fitsNormal || fitsRotated;
}
function matchVariantForSize(input) {
  if (input.candidates.length === 0) return null;
  if (input.rule === "BOTH_EDGES_CEIL") {
    const fitting2 = input.candidates.filter((c) => fitsBothEdges(c, input)).sort((a, b) => a.widthIn * a.heightIn - b.widthIn * b.heightIn);
    if (fitting2.length > 0) return { variantId: fitting2[0].variantId, downscaled: false };
    if (!input.allowSmallerThanDetected) return null;
    const largest = [...input.candidates].sort(
      (a, b) => b.widthIn * b.heightIn - a.widthIn * a.heightIn
    )[0];
    return { variantId: largest.variantId, downscaled: true };
  }
  const wanted = target(input);
  const sorted = [...input.candidates].sort((a, b) => measure(a, input) - measure(b, input));
  const fitting = sorted.find((c) => measure(c, input) >= wanted);
  if (fitting) return { variantId: fitting.variantId, downscaled: false };
  if (!input.allowSmallerThanDetected) return null;
  return { variantId: sorted[sorted.length - 1].variantId, downscaled: true };
}

// src/utils/design-refs.ts
var ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
var ORDER_SUMMARY_TOKEN_REGEX = /^OS_[A-HJ-NP-Za-km-z2-9]{14}$/;
var REJECTION_CUTOFF = 256 - 256 % ALPHABET.length;
function isValidOrderSummaryToken(value) {
  return typeof value === "string" && ORDER_SUMMARY_TOKEN_REGEX.test(value);
}

// src/utils/shopify-cart-properties.ts
var TK_CART_PROPERTIES = {
  /** Internal Design cuid — legacy, still emitted for backward compat. */
  DESIGN_ID: "_tk_design_id",
  /** Public designRefId token — preferred, used by all new flows. */
  DESIGN_REF: "_tk_design_ref",
  /** Which DesignPart this line item represents (sheet id / side key / "composite"). */
  PART_KEY: "_tk_part_key",
  /** TK internal Product cuid the customer was viewing. */
  PRODUCT_REF: "_tk_product_ref",
  /** ProductPreset cuid for this variant. */
  PRESET_ID: "_tk_preset_id",
  /** Designer mode: GANGSHEET | DTF_BY_SIZE | APPAREL | PRODUCT_MOCKUP. */
  DESIGNER_MODE: "_tk_mode",
  /** Stable preview PNG URL (placeholder until job completes). */
  PREVIEW_URL: "_tk_preview_url",
  /** Stable full-export PNG URL (used by fulfillment). */
  EXPORT_URL: "_tk_export_url",
  /**
   * Customer-facing edit URL (Shopify App Proxy → designer). Renamed from
   * `_tk_edit_url` to make the audience explicit next to
   * `_tk_admin_edit_url`. Historical orders may still carry the old key; no
   * consumer reads either key verbatim (the cart block and the admin order
   * block both REBUILD the href from the validated designRef), so no
   * backward-compat alias is required.
   */
  CUSTOMER_EDIT_URL: "_tk_customer_edit_url",
  /**
   * Staff-only admin edit URL — an absolute admin.shopify.com deep link into
   * the embedded app's by-design landing route. Server-built and injected at
   * cart-bridge time (never client-set); the underscore prefix hides it from
   * the storefront cart UI while the admin order detail renders it as a native
   * staff-only link. See buildDesignerAdminEditUrl in design-urls.ts.
   */
  ADMIN_EDIT_URL: "_tk_admin_edit_url",
  /** Total sheet count in the order (gangsheet mode, for display/analytics). */
  SHEET_COUNT: "_tk_sheet_count",
  /** JSON array describing per-sheet breakdown (legacy gangsheet path). */
  ITEMS: "_tk_items",
  /** DesignRevision revisionNo pinned at add-to-cart time. */
  REVISION: "_tk_revision",
  /**
   * Order-summary token ("OS_" + 14 chars) minted per cart LINE at
   * add-to-cart time. The orders/create webhook maps it to the created
   * order via OrderSummaryLink; resolution is token → order → latest
   * revision PDF, so the link survives every edit flow.
   */
  SUMMARY_REF: "_tk_summary_ref",
  /**
   * Clickable order-summary PDF URL shown under the line item in the
   * Shopify admin (same pattern as _tk_preview_url). Stable forever:
   * `{PUBLIC_API}/public/orders/summary/{token}.pdf` — serves a
   * "being prepared" placeholder until the PDF exists, then always
   * streams the LATEST revision's summary.
   */
  SUMMARY_PDF_URL: "_tk_summary_pdf",
  /**
   * ProductOptionSubmission token ("OPS_" + base62). The orders/create webhook
   * resolves it tenant-scoped; visible option labels travel as separate
   * non-underscore properties built by the option engine.
   */
  OPTIONS_REF: "_tk_options_ref",
  /**
   * Reserved for the pricing phase (SHOPIFY_ADDON_LINE): links a hidden
   * "customization fee" cart line back to the parent submission. The webhook
   * ignores it until a Shopify surcharge mode is enabled.
   */
  ADDON_FOR: "_tk_addon_for"
};

// src/product-options/line-item-properties.ts
var MAX_PROPERTY_VALUE_LENGTH = 255;
function formatMoney(cents) {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}$${(abs / 100).toFixed(2)}`;
}
function withSurchargeSuffix(value, surchargeCents) {
  if (surchargeCents === null || surchargeCents === 0) return value;
  const suffix = ` (${surchargeCents > 0 ? "+" : ""}${formatMoney(surchargeCents)})`;
  return `${value}${suffix}`.slice(0, MAX_PROPERTY_VALUE_LENGTH);
}
function buildLineItemProperties(options, submissionToken, summary) {
  const usedNames = /* @__PURE__ */ new Map();
  const visible = options.filter((option) => !option.hiddenInCart && option.displayValue.trim() !== "").map((option) => {
    const base = option.label.trim().slice(0, 100) || "Option";
    const seen = (usedNames.get(base) ?? 0) + 1;
    usedNames.set(base, seen);
    const suffix = seen > 1 ? ` (${seen})` : "";
    const name = `${base.slice(0, 100 - suffix.length)}${suffix}`;
    return {
      name,
      value: withSurchargeSuffix(option.displayValue.trim(), option.surchargeCents).slice(
        0,
        MAX_PROPERTY_VALUE_LENGTH
      )
    };
  });
  const hidden = [
    { name: TK_CART_PROPERTIES.OPTIONS_REF, value: submissionToken }
  ];
  if (summary && isSummaryLink(summary)) {
    hidden.push(
      { name: TK_CART_PROPERTIES.SUMMARY_REF, value: summary.ref },
      { name: TK_CART_PROPERTIES.SUMMARY_PDF_URL, value: summary.pdfUrl }
    );
  }
  return { visible, hidden };
}
function isSummaryLink(summary) {
  return isValidOrderSummaryToken(summary.ref) && summary.pdfUrl.startsWith("https://") && summary.pdfUrl.includes(`/public/orders/summary/${summary.ref}.pdf`) && summary.pdfUrl.length <= MAX_PROPERTY_VALUE_LENGTH;
}
var PUBLIC_UPLOAD_URL = /^https:\/\/[^\s]+\/public\/option-uploads\/[^/\s]+\/(?:original|preview)(?:\?|$)/;
function uploadLinkProperties(uploads, usedNames = /* @__PURE__ */ new Set()) {
  const used = new Set(usedNames);
  const visible = [];
  uploads.forEach((upload, index) => {
    const label = upload.label.trim().slice(0, 80) || "Upload";
    const suffix = uploads.length > 1 ? ` ${index + 1}` : "";
    pushLink(visible, used, `${label}${suffix}`, upload.originalUrl);
    if (upload.previewUrl) {
      pushLink(visible, used, `Preview${suffix}`, upload.previewUrl);
    }
  });
  return visible;
}
function pushLink(visible, used, label, url) {
  if (!PUBLIC_UPLOAD_URL.test(url) || url.length > MAX_PROPERTY_VALUE_LENGTH) return;
  let name = label;
  let sequence = 2;
  while (used.has(name)) {
    name = `${label} (${sequence})`;
    sequence += 1;
  }
  used.add(name);
  visible.push({ name, value: url });
}

// src/product-options/tokens.ts
var BASE62 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
var TOKEN_BODY_LENGTH = 22;
function randomBody(length) {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < length; i++) out += BASE62[bytes[i] % BASE62.length];
  return out;
}
function generateOptionSubmissionToken() {
  return `OPS_${randomBody(TOKEN_BODY_LENGTH)}`;
}
function generateOptionUploadToken() {
  return `OU_${randomBody(TOKEN_BODY_LENGTH)}`;
}
function isValidOptionsRefToken(token) {
  return OPTION_SUBMISSION_TOKEN_PATTERN.test(token);
}
function isValidOptionUploadToken(token) {
  return OPTION_UPLOAD_TOKEN_PATTERN.test(token);
}

// src/product-options/registry.ts
var PHASE1_SUPPORTED_OPTION_TYPES = [
  "TEXT",
  "TEXTAREA",
  "STRING",
  "NUMBER",
  "CHECKBOX",
  "CHECKBOX_GROUP",
  "BOOLEAN",
  "RADIO",
  "SELECT",
  "SWATCH",
  "BUTTON_GROUP",
  "DATE",
  "CUSTOM_SIZE",
  "INFO",
  "FILE_UPLOAD",
  "IMAGE_UPLOAD_SIZED",
  "GANGSHEET_UPLOAD_SIZED"
];
var PICKER_OPTION_TYPES = [
  "TEXT",
  "TEXTAREA",
  "NUMBER",
  "CHECKBOX",
  "CHECKBOX_GROUP",
  "RADIO",
  "SELECT",
  "SWATCH",
  "BUTTON_GROUP",
  "DATE",
  "CUSTOM_SIZE",
  "INFO",
  "FILE_UPLOAD"
];
function pickerOptionTypesFor(template) {
  if (template.designerMode !== "NONE") return PICKER_OPTION_TYPES;
  const extra = [];
  if (template.type === "DTF_BY_SIZE") {
    extra.push("IMAGE_UPLOAD_SIZED", "GANGSHEET_UPLOAD_SIZED");
  } else if (template.type === "GANGSHEET") {
    extra.push("GANGSHEET_UPLOAD_SIZED");
  }
  return extra.length === 0 ? PICKER_OPTION_TYPES : [...PICKER_OPTION_TYPES, ...extra];
}
var LEGACY_OPTION_TYPES = ["STRING", "BOOLEAN"];
function isLegacyOptionType(optionType) {
  return LEGACY_OPTION_TYPES.includes(optionType);
}
var PHASE1_SET = new Set(PHASE1_SUPPORTED_OPTION_TYPES);
function isPhase1OptionType(optionType) {
  return PHASE1_SET.has(optionType);
}
var registry = /* @__PURE__ */ new Map();
function registerOptionType(definition) {
  registry.set(definition.type, definition);
}
function getOptionType(type) {
  return registry.get(type);
}
function listOptionTypes() {
  return [...registry.values()];
}

// src/product-options/plugins.ts
import { z as z2 } from "zod";

// src/product-options/selection.ts
function isOptionSelectionFilled(selection) {
  if (!selection) return false;
  if ((selection.uploadTokens?.length ?? 0) > 0) return true;
  return (selection.values ?? []).some((value) => {
    const trimmed = value.trim();
    return trimmed !== "" && trimmed !== "false";
  });
}
var isFilled = isOptionSelectionFilled;
function unsafeCustomerPattern(pattern) {
  if (pattern.length > 200) return "Validation pattern is too long.";
  try {
    new RegExp(pattern);
  } catch {
    return "Validation pattern is not a valid regular expression.";
  }
  if (/\((?:[^()\\]|\\.)*[+*](?:[^()\\]|\\.)*\)(?:[+*]|\{\d)/.test(pattern)) {
    return "Validation pattern is too expensive to check.";
  }
  return null;
}
function isRealIsoDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

// src/product-options/plugins.ts
var NO_UPLOAD = {
  supportsVersions: false,
  supportsRecent: false,
  supportsGallery: false,
  supportsImageTools: false,
  bindsVariantAxis: false,
  usesChoices: false,
  usesPricing: true
};
function requiredValueError(selection, required) {
  if (!required) return null;
  if (!selection || selection.values.every((v) => v.trim() === "")) {
    return "This field is required.";
  }
  return null;
}
function registerTextFamily(type, defaults) {
  registerOptionType({
    type: ProductOptionType[type],
    configSchema: textConfigSchema,
    defaultConfig: defaults,
    capabilities: NO_UPLOAD,
    processingPipeline: [],
    validate: (selection, config, required) => {
      const missing = requiredValueError(selection, required);
      if (missing) return missing;
      if ((selection?.values.length ?? 0) > 1) return "Enter a single value.";
      const raw = selection?.values[0]?.trim() ?? "";
      if (raw === "") return null;
      const value = applyTextTransform(raw, config.textTransform);
      const hardCap = type === "TEXTAREA" ? 2e4 : 2e3;
      const limit = Math.min(config.maxLength, hardCap);
      if (value.length < config.minLength) return `Enter at least ${config.minLength} characters.`;
      if (value.length > limit) return `Keep it under ${limit} characters.`;
      if (config.pattern) {
        if (unsafeCustomerPattern(config.pattern)) return "Invalid validation pattern configured.";
        if (!new RegExp(config.pattern).test(value)) {
          return "The value does not match the required format.";
        }
      }
      return null;
    }
  });
}
registerTextFamily("TEXT", {
  minLength: 0,
  maxLength: 200,
  pattern: null,
  showCounter: false,
  textTransform: "NONE"
});
registerTextFamily("TEXTAREA", {
  minLength: 0,
  maxLength: 2e3,
  pattern: null,
  showCounter: true,
  textTransform: "NONE"
});
registerTextFamily("STRING", {
  minLength: 0,
  maxLength: 500,
  pattern: null,
  showCounter: false,
  textTransform: "NONE"
});
function registerNumber(type) {
  registerOptionType({
    type: ProductOptionType[type],
    configSchema: numberConfigSchema,
    defaultConfig: { min: null, max: null, step: 1 },
    capabilities: NO_UPLOAD,
    processingPipeline: [],
    validate: (selection, config, required) => {
      const missing = requiredValueError(selection, required);
      if (missing) return missing;
      if ((selection?.values.length ?? 0) > 1) return "Enter a single value.";
      const raw = selection?.values[0]?.trim() ?? "";
      if (raw === "") return null;
      if (raw.length > 64) return "Enter a shorter number.";
      const value = Number(raw);
      if (!Number.isFinite(value)) return "Enter a number.";
      if (config.min !== null && config.min !== void 0 && value < config.min) {
        return `Minimum is ${config.min}.`;
      }
      if (config.max !== null && config.max !== void 0 && value > config.max) {
        return `Maximum is ${config.max}.`;
      }
      const step = config.step ?? 1;
      const origin = config.min ?? 0;
      const steps = (value - origin) / step;
      if (Math.abs(steps - Math.round(steps)) > 1e-6) {
        return `Use steps of ${step}.`;
      }
      return null;
    }
  });
}
registerNumber("NUMBER");
var booleanConfigSchema = z2.object({});
function validateCheckbox(selection, required) {
  if (!required) return null;
  return selection?.values[0] === "true" ? null : "This field is required.";
}
registerOptionType({
  type: ProductOptionType.CHECKBOX,
  configSchema: checkboxConfigSchema,
  defaultConfig: {},
  capabilities: NO_UPLOAD,
  processingPipeline: [],
  validate: (selection, _config, required) => validateCheckbox(selection, required)
});
registerOptionType({
  type: ProductOptionType.BOOLEAN,
  configSchema: booleanConfigSchema,
  defaultConfig: {},
  capabilities: NO_UPLOAD,
  processingPipeline: [],
  validate: (selection, _config, required) => validateCheckbox(selection, required)
});
var CHOICE_TYPES2 = ["RADIO", "SELECT", "BUTTON_GROUP"];
registerOptionType({
  type: ProductOptionType.SWATCH,
  configSchema: swatchConfigSchema,
  defaultConfig: { swatchSize: "SMALL" },
  capabilities: { ...NO_UPLOAD, usesChoices: true },
  processingPipeline: [],
  validate: (selection, _config, required) => {
    const missing = requiredValueError(selection, required);
    if (missing) return missing;
    const filled = selection?.values.filter((value) => value.trim() !== "") ?? [];
    if (filled.length > 1) return "Choose one option.";
    return null;
  }
});
for (const type of CHOICE_TYPES2) {
  registerOptionType({
    type: ProductOptionType[type],
    configSchema: z2.object({}),
    defaultConfig: {},
    capabilities: { ...NO_UPLOAD, usesChoices: true },
    processingPipeline: [],
    validate: (selection, _config, required) => {
      const missing = requiredValueError(selection, required);
      if (missing) return missing;
      const filled = selection?.values.filter((value) => value.trim() !== "") ?? [];
      if (filled.length > 1) return "Choose one option.";
      return null;
    }
  });
}
registerOptionType({
  type: ProductOptionType.CHECKBOX_GROUP,
  configSchema: checkboxGroupConfigSchema,
  defaultConfig: { minSelections: 0, maxSelections: null },
  capabilities: { ...NO_UPLOAD, usesChoices: true },
  processingPipeline: [],
  validate: (selection, config, required) => {
    const picked = selection?.values.filter((value) => value.trim() !== "") ?? [];
    if (required && picked.length === 0) return "Choose at least one option.";
    if (picked.length > 0 && picked.length < config.minSelections) {
      return `Choose at least ${config.minSelections}.`;
    }
    if (config.maxSelections != null && picked.length > config.maxSelections) {
      return `Choose at most ${config.maxSelections}.`;
    }
    return null;
  }
});
registerOptionType({
  type: ProductOptionType.DATE,
  configSchema: dateConfigSchema,
  defaultConfig: { minDate: null, maxDate: null, blackoutDates: [], displayFormat: "LOCALE" },
  capabilities: NO_UPLOAD,
  processingPipeline: [],
  validate: (selection, config, required) => {
    const missing = requiredValueError(selection, required);
    if (missing) return missing;
    if ((selection?.values.length ?? 0) > 1) return "Enter a single date.";
    const value = selection?.values[0]?.trim() ?? "";
    if (value === "") return null;
    if (!isRealIsoDate(value)) return "Enter a real calendar date (YYYY-MM-DD).";
    if (config.minDate && value < config.minDate) return `Earliest date is ${config.minDate}.`;
    if (config.maxDate && value > config.maxDate) return `Latest date is ${config.maxDate}.`;
    if (config.blackoutDates.includes(value)) return "That date is not available.";
    return null;
  }
});
registerOptionType({
  type: ProductOptionType.INFO,
  configSchema: infoConfigSchema,
  defaultConfig: { body: "" },
  capabilities: { ...NO_UPLOAD, usesPricing: false },
  processingPipeline: [],
  validate: () => null
});
function customSizeAxisError(label, raw, min, max, step, unit) {
  const trimmed = raw?.trim() ?? "";
  if (trimmed === "") return `Enter a ${label.toLowerCase()}.`;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return `Enter a ${label.toLowerCase()}.`;
  if (value < min || value > max) {
    return `${label} must be between ${min} and ${max} ${unit}.`;
  }
  const steps = (value - min) / step;
  if (Math.abs(steps - Math.round(steps)) > 1e-6) {
    return `${label} uses steps of ${step} ${unit}.`;
  }
  return null;
}
registerOptionType({
  type: ProductOptionType.CUSTOM_SIZE,
  configSchema: customSizeConfigSchema,
  defaultConfig: {
    unit: "in",
    dimensions: 2,
    labels: { x: "Width", y: "Height" },
    min: { x: 1, y: 1 },
    max: { x: 120, y: 120 },
    step: { x: 0.5, y: 0.5 },
    defaults: {}
  },
  capabilities: NO_UPLOAD,
  processingPipeline: [],
  validate: (selection, config, required) => {
    const axes = config.dimensions === 3 ? [
      ["x", config.labels.x, selection?.values[0]],
      ["y", config.labels.y, selection?.values[1]],
      ["z", config.labels.z ?? "Depth", selection?.values[2]]
    ] : [
      ["x", config.labels.x, selection?.values[0]],
      ["y", config.labels.y, selection?.values[1]]
    ];
    const empty = axes.every(([, , raw]) => (raw?.trim() ?? "") === "");
    if (empty) return required ? "This field is required." : null;
    for (const [axis, label, raw] of axes) {
      const min = config.min[axis];
      const max = config.max[axis];
      const step = config.step[axis];
      if (min == null || max == null || step == null) return "This size option is misconfigured.";
      const error = customSizeAxisError(label, raw, min, max, step, config.unit);
      if (error) return error;
    }
    return null;
  }
});
var FILE_UPLOAD_DEFAULT = {
  accept: { mimeTypes: ["application/pdf", "image/png", "image/jpeg"], maxFileMb: 50, maxFiles: 5 },
  sources: ["DEVICE", "RECENT"]
};
registerOptionType({
  type: ProductOptionType.FILE_UPLOAD,
  configSchema: uploadConfigSchema,
  defaultConfig: FILE_UPLOAD_DEFAULT,
  capabilities: {
    ...NO_UPLOAD,
    supportsVersions: true,
    supportsRecent: true,
    supportsGallery: false
  },
  processingPipeline: ["SCAN", "HASH", "THUMB"],
  validate: (selection, config, required) => {
    const count = selection?.uploadTokens?.length ?? 0;
    if (required && count === 0) return "Upload at least one file.";
    if (count > config.accept.maxFiles) return `Up to ${config.accept.maxFiles} files.`;
    return null;
  }
});
registerOptionType({
  type: ProductOptionType.IMAGE_UPLOAD_SIZED,
  configSchema: uploadConfigSchema,
  defaultConfig: {
    accept: { mimeTypes: ["image/png", "image/jpeg", "image/webp"], maxFileMb: 150, maxFiles: 25 },
    sources: ["DEVICE", "RECENT"],
    sizing: {
      assumedDpi: 300,
      trim: true,
      minDpiWarn: 150,
      minSizeIn: 0.1,
      maxWidthIn: 22,
      maxHeightIn: 240,
      stepIn: 0.1,
      dpiSource: "EMBEDDED_IF_PLAUSIBLE",
      dpiGood: 250,
      dpiFair: 150,
      trimMode: "ALPHA",
      trimAlphaThreshold: 8,
      allowResize: true,
      allowDetectedSize: true,
      allowStretch: true,
      presets: []
    },
    sizePricing: { mode: "AREA_RATE", pricingVariantId: null },
    variantBinding: {
      optionTypeId: "",
      defaultVariantRule: "LONGEST_EDGE_CEIL",
      customerCanChangeVariant: false,
      allowSmallerThanDetected: false
    },
    imageTools: { bgRemoval: "OFF", upscale: "OFF", editor: "ON" },
    printRender: {
      ratioLocked: true,
      fit: "CONTAIN",
      allowUpscale: true,
      minEffectiveDpiWarn: 150
    }
  },
  capabilities: {
    supportsVersions: true,
    supportsRecent: true,
    supportsGallery: false,
    supportsImageTools: true,
    bindsVariantAxis: true,
    usesChoices: false,
    usesPricing: true
  },
  processingPipeline: ["SCAN", "HASH", "METADATA", "TRIM", "PRINT_RENDER", "PREVIEW", "THUMB"],
  validate: (selection, config, required) => validateSizedUpload(selection, config, required, null)
});
registerOptionType({
  type: ProductOptionType.GANGSHEET_UPLOAD_SIZED,
  configSchema: uploadConfigSchema,
  defaultConfig: {
    accept: { mimeTypes: ["image/png", "image/jpeg", "image/webp"], maxFileMb: 500, maxFiles: 10 },
    sources: ["DEVICE", "RECENT"],
    sizing: {
      assumedDpi: 300,
      trim: true,
      minDpiWarn: 150,
      minSizeIn: 0.1,
      maxWidthIn: 22,
      maxHeightIn: 240,
      stepIn: 0.1,
      dpiSource: "EMBEDDED_IF_PLAUSIBLE",
      dpiGood: 250,
      dpiFair: 150,
      trimMode: "ALPHA",
      trimAlphaThreshold: 8,
      allowResize: false,
      allowDetectedSize: true,
      allowStretch: false,
      presets: []
    },
    sizePricing: { mode: "LONGEST_EDGE_RATE", pricingVariantId: null },
    variantBinding: {
      optionTypeId: "",
      defaultVariantRule: "HEIGHT_CEIL",
      customerCanChangeVariant: false,
      allowSmallerThanDetected: false,
      fixedWidthIn: 22
    },
    gangsheet: { widthToleranceIn: 0.25 },
    printRender: {
      ratioLocked: true,
      fit: "CONTAIN",
      allowUpscale: false,
      minEffectiveDpiWarn: 150
    }
  },
  capabilities: {
    supportsVersions: true,
    supportsRecent: true,
    supportsGallery: false,
    supportsImageTools: false,
    bindsVariantAxis: true,
    usesChoices: false,
    usesPricing: true
  },
  processingPipeline: ["SCAN", "HASH", "METADATA", "TRIM", "PRINT_RENDER", "THUMB"],
  validate: (selection, config, required) => validateSizedUpload(selection, config, required, "gangsheet")
});
function validateSizedUpload(selection, config, required, kind) {
  const parsed = uploadConfigSchema.safeParse(config);
  if (!parsed.success) return "Invalid upload configuration.";
  const upload = parsed.data;
  const bounds = sizingBounds(upload);
  const message = validateSizedValues({
    values: selection?.values ?? [],
    required,
    uploadCount: selection?.uploadTokens?.length ?? 0,
    maxFiles: upload.accept.maxFiles,
    bounds: kind === "gangsheet" ? { ...bounds, minSizeIn: Math.min(bounds.minSizeIn, 0.01) } : bounds,
    allowResize: kind === "gangsheet" ? false : upload.sizing?.allowResize ?? true,
    allowStretch: kind === "gangsheet" ? false : upload.sizing?.allowStretch ?? true,
    fixedWidthIn: kind === "gangsheet" ? upload.variantBinding?.fixedWidthIn ?? 22 : null
  });
  if (kind === "gangsheet" && message === "Upload an image.") return "Upload a gang sheet.";
  return message;
}

// src/product-options/upload-policy.ts
var PLATFORM_DEFAULT_MIMES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "application/pdf"
];
var ALWAYS_DENIED = /* @__PURE__ */ new Set([
  "application/x-msdownload",
  "application/x-executable",
  "application/x-dosexec",
  "application/zip",
  "application/x-zip-compressed",
  "application/gzip",
  "application/x-tar",
  "application/x-rar-compressed",
  "application/java-archive",
  "application/javascript",
  "text/html",
  "text/javascript",
  "image/svg+xml"
]);
var SVG = "image/svg+xml";
function normalizeMime(value) {
  return value.trim().toLowerCase();
}
var PLATFORM_NEVER = new Set(
  [...ALWAYS_DENIED].filter((mime) => mime !== SVG)
);
function normalizePlatformMimeSuperset(mimes) {
  const normalized = [
    ...new Set(mimes.map(normalizeMime).filter((mime) => mime.length > 0))
  ];
  const rejected = normalized.filter(
    (mime) => PLATFORM_NEVER.has(mime) || !/^[\w.+-]+\/[\w.+-]+$/.test(mime)
  );
  if (rejected.length > 0) {
    return {
      ok: false,
      message: `These file types cannot be allowed: ${rejected.join(", ")}.`
    };
  }
  return { ok: true, mimes: normalized };
}
function allowedMimesForOption(input) {
  const superset = input.platformSuperset.length > 0 ? input.platformSuperset.map(normalizeMime) : [...PLATFORM_DEFAULT_MIMES];
  const supersetSet = new Set(superset.filter((mime) => !ALWAYS_DENIED.has(mime)));
  const accepted = input.optionMimes.map(normalizeMime).filter((mime) => supersetSet.has(mime) && !ALWAYS_DENIED.has(mime));
  const optionListsSvg = input.optionMimes.some((mime) => normalizeMime(mime) === SVG);
  const platformAllowsSvg = input.platformSuperset.some((mime) => normalizeMime(mime) === SVG);
  const svgOption = input.optionType === "FILE_UPLOAD" || input.optionType === "IMAGE_UPLOAD_SIZED" || input.optionType === "GANGSHEET_UPLOAD_SIZED";
  if (svgOption && optionListsSvg && platformAllowsSvg) {
    accepted.push(SVG);
  }
  return [...new Set(accepted)];
}
function isMimeAllowed(mime, allowed) {
  return allowed.includes(normalizeMime(mime).split(";")[0]?.trim() ?? "");
}
var DECLARED_MIME_ALIASES = {
  "image/jpg": "image/jpeg",
  "image/pjpeg": "image/jpeg",
  "image/x-citrix-jpeg": "image/jpeg",
  "image/x-png": "image/png",
  "image/apng": "image/png",
  "image/vnd.mozilla.apng": "image/png",
  "image/x-citrix-png": "image/png",
  "image/tif": "image/tiff",
  "image/x-tiff": "image/tiff"
};
var EXTENSION_MIME = {
  png: "image/png",
  apng: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
  pdf: "application/pdf",
  tif: "image/tiff",
  tiff: "image/tiff",
  svg: "image/svg+xml",
  avif: "image/avif"
};
function stripDeclaredMime(mime) {
  const base = normalizeMime(mime).split(";")[0]?.trim() ?? "";
  return DECLARED_MIME_ALIASES[base] ?? base;
}
function canonicalDeclaredMime(declared, fileName) {
  const mime = stripDeclaredMime(declared);
  const undetected = mime.length === 0 || mime === "application/octet-stream" || mime === "application/binary" || mime === "binary/octet-stream";
  if (!undetected) return mime;
  const base = fileName.split(/[/\\]/).pop() ?? "";
  const ext = base.includes(".") ? base.split(".").pop()?.toLowerCase() ?? "" : "";
  return EXTENSION_MIME[ext] ?? mime;
}
function uploadAcceptAttribute(mimes) {
  const extras = [];
  for (const mime of mimes.map((item) => stripDeclaredMime(item))) {
    if (mime === "image/png") extras.push(".png", ".apng");
    else if (mime === "image/jpeg") extras.push(".jpg", ".jpeg");
    else if (mime === "image/webp") extras.push(".webp");
    else if (mime === "image/gif") extras.push(".gif");
    else if (mime === "application/pdf") extras.push(".pdf");
    else if (mime === "image/tiff") extras.push(".tif", ".tiff");
    else if (mime === "image/avif") extras.push(".avif");
    else if (mime === "image/svg+xml") extras.push(".svg");
  }
  return [...mimes, ...extras].join(",");
}
function sanitizeUploadFileName(name) {
  const base = name.split(/[/\\]/).pop() ?? "file";
  const cleaned = base.replace(/[^\w.\- ()]+/g, "_").replace(/^\.+/, "").slice(0, 180);
  return cleaned.length > 0 ? cleaned : "file";
}
function effectiveMaxBytes(input) {
  const mb = Math.min(input.optionMaxMb, input.platformCeilingMb, input.envCeilingMb);
  return Math.floor(mb * 1024 * 1024);
}
function partCountFor(sizeBytes, partSizeBytes) {
  if (sizeBytes < 1 || partSizeBytes < 1) return 0;
  return Math.ceil(sizeBytes / partSizeBytes);
}

// src/product-options/upload-failure.ts
var OptionUploadFailureCode = {
  EMPTY: "EMPTY",
  SIZE_MISMATCH: "SIZE_MISMATCH",
  SIZE_LIMIT: "SIZE_LIMIT",
  TYPE_NOT_ALLOWED: "TYPE_NOT_ALLOWED",
  DECODE_FAILED: "DECODE_FAILED",
  PIXEL_LIMIT: "PIXEL_LIMIT",
  THUMB_FAILED: "THUMB_FAILED",
  MALWARE: "MALWARE",
  SCAN_REQUIRED: "SCAN_REQUIRED",
  SCAN_PROVIDER: "SCAN_PROVIDER",
  ABANDONED: "ABANDONED",
  STUCK: "STUCK",
  NO_OBJECT: "NO_OBJECT",
  INVALID_CONFIG: "INVALID_CONFIG",
  DOWNLOAD_MISMATCH: "DOWNLOAD_MISMATCH",
  PROCESSING_FAILED: "PROCESSING_FAILED",
  WIDTH_EXCEEDS: "WIDTH_EXCEEDS"
};
var CODES = new Set(Object.values(OptionUploadFailureCode));
function optionUploadFailureCode(raw) {
  if (raw && CODES.has(raw)) return raw;
  return OptionUploadFailureCode.PROCESSING_FAILED;
}
var MESSAGES = {
  EMPTY: "The uploaded file is empty.",
  SIZE_MISMATCH: "The stored file size does not match the upload.",
  SIZE_LIMIT: "The file is larger than the allowed size.",
  TYPE_NOT_ALLOWED: "This file type is not accepted.",
  DECODE_FAILED: "The image could not be read.",
  PIXEL_LIMIT: "The image exceeds the pixel limit.",
  THUMB_FAILED: "A preview could not be created for this image.",
  MALWARE: "The file failed the malware scan.",
  SCAN_REQUIRED: "Malware scanning is required but no scanner is configured.",
  SCAN_PROVIDER: "The configured malware scanner is not supported.",
  ABANDONED: "The upload was not finished.",
  STUCK: "Processing did not finish. Please upload the file again.",
  NO_OBJECT: "The uploaded file is missing.",
  INVALID_CONFIG: "This option is not configured for uploads.",
  DOWNLOAD_MISMATCH: "The stored file could not be read completely.",
  PROCESSING_FAILED: "The file could not be processed.",
  WIDTH_EXCEEDS: "The image is wider than this gang sheet allows."
};
var UPLOAD_MAX_PIXELS_DEFAULT = 268402689;
function resolveUploadMaxPixels(raw) {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 1) return UPLOAD_MAX_PIXELS_DEFAULT;
  return Math.min(Math.floor(parsed), UPLOAD_MAX_PIXELS_DEFAULT);
}
var SIZED_UPLOAD_MAX_PIXELS_DEFAULT = 5e8;
var SIZED_UPLOAD_MAX_PIXELS_HARD = 6e8;
function resolveSizedUploadMaxPixels(raw) {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 1) return SIZED_UPLOAD_MAX_PIXELS_DEFAULT;
  return Math.min(Math.floor(parsed), SIZED_UPLOAD_MAX_PIXELS_HARD);
}
function optionUploadFailureMessage(code, limits) {
  if (!code) return null;
  if (code === OptionUploadFailureCode.PIXEL_LIMIT && limits?.maxPixels && limits.maxPixels > 0) {
    const megapixels = Math.max(1, Math.round(limits.maxPixels / 1e6));
    return `The image exceeds ${megapixels} megapixels.`;
  }
  if (code === OptionUploadFailureCode.SIZE_LIMIT && limits?.maxFileMb && limits.maxFileMb > 0) {
    return `The file must be ${limits.maxFileMb} MB or smaller.`;
  }
  if (CODES.has(code)) return MESSAGES[code];
  return MESSAGES.PROCESSING_FAILED;
}

// src/product-options/order-display.ts
var PREVIEWABLE_IMAGE = /^image\/(png|jpeg|webp|gif)$/;
function variantAxisLabel(snapshot) {
  if (!snapshot || typeof snapshot !== "object" || Array.isArray(snapshot)) return null;
  const options = snapshot.options;
  if (!Array.isArray(options)) return null;
  const parts = [];
  for (const row of options) {
    if (!row || typeof row !== "object") continue;
    const record = row;
    const type = typeof record.type === "string" ? record.type : record.name;
    if (typeof type !== "string" || typeof record.value !== "string") continue;
    const name = type.trim();
    const value = record.value.trim();
    if (!name || !value || name.startsWith("_")) continue;
    parts.push(`${name}: ${value}`);
  }
  return parts.length > 0 ? parts.join(" / ") : null;
}
var RAW_SIZED_VALUE = /^(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)(?:,\s*([^,\s]+))?(?:,\s*stretch)?$/;
var PRETTY_SIZED_VALUE = /(\d+(?:\.\d+)?)\s×\s(\d+(?:\.\d+)?)\s+in/;
function inchesPair(width, height) {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  return `${formatInches(width)} \xD7 ${formatInches(height)} in`;
}
function sizedInchesFromValue(value) {
  const raw = value.trim().match(RAW_SIZED_VALUE);
  if (raw) {
    const width2 = Number(raw[1]);
    const height2 = Number(raw[2]);
    if (Number.isFinite(width2) && Number.isFinite(height2)) return { width: width2, height: height2 };
  }
  const pretty = value.match(PRETTY_SIZED_VALUE);
  if (!pretty) return null;
  const width = Number(pretty[1]);
  const height = Number(pretty[2]);
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  return { width, height };
}
function selectionInches(customization) {
  if (!customization || typeof customization !== "object" || Array.isArray(customization)) {
    return null;
  }
  const selections = customization.selections;
  if (!Array.isArray(selections)) return null;
  for (const row of selections) {
    if (!row || typeof row !== "object") continue;
    const value = row.value;
    if (typeof value !== "string") continue;
    const size = sizedInchesFromValue(value);
    if (size) return size;
  }
  return null;
}
function chargedInches(customization) {
  if (!customization || typeof customization !== "object" || Array.isArray(customization)) {
    return null;
  }
  const pricing = customization.pricing;
  if (!pricing || typeof pricing !== "object" || Array.isArray(pricing)) return null;
  const row = pricing;
  const width = typeof row.formulaWidth === "number" ? row.formulaWidth : Number(row.formulaWidth);
  const height = typeof row.formulaHeight === "number" ? row.formulaHeight : Number(row.formulaHeight);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
  return { width, height };
}
function orderedSizeText(customization) {
  const size = selectionInches(customization);
  return size ? inchesPair(size.width, size.height) : null;
}
function printSizeChangedNote(customization) {
  const print = selectionInches(customization);
  const charged = chargedInches(customization);
  if (!print || !charged) return null;
  const same = Math.abs(print.width - charged.width) <= 0.01 && Math.abs(print.height - charged.height) <= 0.01;
  return same ? null : "(updated, price unchanged)";
}
function formatOrderedOptionValue(value) {
  const raw = value.trim().match(RAW_SIZED_VALUE);
  if (!raw?.[3]) return value;
  const size = inchesPair(Number(raw[1]), Number(raw[2]));
  if (!size) return value;
  if (raw[3] === "custom") return `Custom: ${size}`;
  if (raw[3] === "detected") return `Detected: ${size}`;
  return size;
}
function orderedVariantLabel(snapshot, customization) {
  const base = variantAxisLabel(snapshot);
  if (!base) return null;
  const size = orderedSizeText(customization);
  if (!size) return base;
  const note = printSizeChangedNote(customization);
  return base.split(" / ").map((part, index) => {
    const name = part.includes(": ") ? part.slice(part.indexOf(": ") + 2) : part;
    const labeled = `${name}: ${size}`;
    return index === 0 && note ? `${labeled} ${note}` : labeled;
  }).join(" / ");
}
function optionUploadPreviewFiles(customization) {
  if (!customization || typeof customization !== "object" || Array.isArray(customization)) {
    return [];
  }
  const uploads = customization.uploads;
  if (!Array.isArray(uploads)) return [];
  const files = [];
  for (const raw of uploads) {
    if (!raw || typeof raw !== "object") continue;
    const record = raw;
    if (typeof record.mimeType !== "string" || !PREVIEWABLE_IMAGE.test(record.mimeType)) continue;
    const key = [record.keys?.preview, record.keys?.thumb, record.keys?.original].find(
      (item) => typeof item === "string" && item.length > 0
    );
    if (!key) continue;
    const label = typeof record.fileName === "string" && record.fileName.trim().length > 0 ? record.fileName.trim() : "Uploaded file";
    files.push({ key, label });
  }
  return files;
}

// src/product-options/upload-version.ts
var SIZED_PRINT_TYPES = /* @__PURE__ */ new Set(["IMAGE_UPLOAD_SIZED", "GANGSHEET_UPLOAD_SIZED"]);
var VERSION_QUERY = /([?&])v=\d+/;
function withVersion(url, versionNo) {
  if (VERSION_QUERY.test(url)) {
    return url.replace(VERSION_QUERY, `$1v=${versionNo}`);
  }
  return `${url}${url.includes("?") ? "&" : "?"}v=${versionNo}`;
}
function rewriteStoredUploadVersion(customization, update) {
  if (!customization || typeof customization !== "object" || Array.isArray(customization)) {
    return customization;
  }
  const root = customization;
  if (!Array.isArray(root.uploads)) return customization;
  let changed = false;
  let matchedKey = null;
  let matchedType = null;
  const uploads = root.uploads.map((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return item;
    const row = item;
    if (row.token !== update.token) return item;
    changed = true;
    matchedKey = typeof row.optionKey === "string" ? row.optionKey : null;
    matchedType = typeof row.optionType === "string" ? row.optionType : null;
    const urls = row.urls && typeof row.urls === "object" && !Array.isArray(row.urls) ? { ...row.urls } : null;
    if (urls) {
      for (const key of Object.keys(urls)) {
        const value = urls[key];
        if (typeof value === "string" && value.length > 0) {
          urls[key] = withVersion(value, update.versionNo);
        }
      }
    }
    const previousKeys = row.keys && typeof row.keys === "object" && !Array.isArray(row.keys) ? row.keys : {};
    return {
      ...row,
      versionNoAtOrder: update.versionNo,
      ...update.fileName != null ? { fileName: update.fileName } : {},
      ...update.mimeType != null ? { mimeType: update.mimeType } : {},
      ...update.sizeBytes != null ? { sizeBytes: update.sizeBytes } : {},
      ...update.widthPx != null ? { originalWidthPx: update.widthPx } : {},
      ...update.heightPx != null ? { originalHeightPx: update.heightPx } : {},
      ...update.printWidthIn != null ? { printWidthIn: update.printWidthIn } : {},
      ...update.printHeightIn != null ? { printHeightIn: update.printHeightIn } : {},
      ...update.keys ? { keys: { ...previousKeys, ...update.keys } } : {},
      ...urls ? { urls } : {}
    };
  });
  if (!changed) return customization;
  const widthIn = update.printWidthIn;
  const heightIn = update.printHeightIn;
  const hasPrintSize = widthIn != null && heightIn != null && widthIn > 0 && heightIn > 0;
  const sizedLine = matchedType == null || SIZED_PRINT_TYPES.has(matchedType);
  return {
    ...root,
    uploads,
    ...hasPrintSize && sizedLine ? {
      customSize: {
        ...sizeRecord(root.customSize),
        widthIn,
        heightIn
      },
      selections: rewriteSelectionSize(root.selections, matchedKey, widthIn, heightIn)
    } : {}
  };
}
function sizeRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return { ...value };
}
function rewriteSelectionSize(selections, optionKey, widthIn, heightIn) {
  if (!optionKey || !Array.isArray(selections)) return selections;
  const preset = "detected";
  const text = `${preset === "detected" ? "Detected" : "Custom"}: ${formatInches(widthIn)} \xD7 ${formatInches(heightIn)} in`;
  return selections.map((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return item;
    const row = item;
    const key = typeof row.key === "string" ? row.key : typeof row.optionKey === "string" ? row.optionKey : null;
    if (key !== optionKey || !Array.isArray(row.values) || row.values.length < 2) return item;
    const values = row.values.slice(0, 3);
    values[0] = String(widthIn);
    values[1] = String(heightIn);
    values[2] = preset;
    return { ...row, values, value: text };
  });
}
function positiveInches(value) {
  const parsed = typeof value === "number" ? value : typeof value === "string" ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}
function firstPositive(values) {
  for (const value of values) {
    if (value != null && value > 0) return value;
  }
  return null;
}
function selectedReplacementWidthIn(input) {
  const ordered = firstPositive(
    input.orderCustomizations.map((row) => widthFromOrder(row, input.token, input.optionKey))
  );
  if (ordered != null) return ordered;
  const submitted = firstPositive(
    input.submissionSelections.map((row) => widthFromSubmission(row, input.optionKey))
  );
  if (submitted != null) return submitted;
  const drafted = widthFromDraft(input.draft);
  if (drafted != null) return drafted;
  return positiveInches(input.previousPrintWidthIn);
}
function widthFromOrder(customization, token, optionKey) {
  if (!customization || typeof customization !== "object" || Array.isArray(customization)) return null;
  const root = customization;
  let tokenFound = false;
  if (Array.isArray(root.uploads)) {
    for (const item of root.uploads) {
      if (!item || typeof item !== "object" || Array.isArray(item)) continue;
      const row = item;
      if (row.token !== token) continue;
      tokenFound = true;
      const width = positiveInches(row.printWidthIn);
      if (width != null) return width;
    }
  }
  if (!tokenFound) return null;
  return widthFromSelectionList(root.selections, optionKey) ?? positiveInches(sizeRecord(root.customSize).widthIn);
}
function widthFromSubmission(payload, optionKey) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  const root = payload;
  return widthFromSelectionList(root.selections, optionKey);
}
function widthFromSelectionList(selections, optionKey) {
  if (!optionKey || !Array.isArray(selections)) return null;
  for (const item of selections) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const row = item;
    const key = typeof row.key === "string" ? row.key : typeof row.optionKey === "string" ? row.optionKey : null;
    if (key !== optionKey || !Array.isArray(row.values)) continue;
    const width = positiveInches(row.values[0]);
    if (width != null) return width;
  }
  return null;
}
function widthFromDraft(draft) {
  if (!draft || typeof draft !== "object" || Array.isArray(draft)) return null;
  return positiveInches(draft.widthIn);
}
export {
  DATE_DISPLAY_FORMATS,
  DPI_SOURCES,
  MAX_PROPERTY_VALUE_LENGTH,
  OPTION_SUBMISSION_TOKEN_PATTERN,
  OPTION_UPLOAD_TOKEN_PATTERN,
  OptionUploadFailureCode,
  PHASE1_SUPPORTED_OPTION_TYPES,
  PICKER_OPTION_TYPES,
  PLATFORM_DEFAULT_MIMES,
  PRESET_PRICING,
  PRODUCT_OPTIONS_ELIGIBLE_DESIGNER_MODE,
  PRODUCT_OPTIONS_SCHEMA_VERSION,
  PRODUCT_OPTION_CHOICE_KEY_PATTERN,
  ProductOptionType,
  SIZED_STRETCH_FLAG,
  SIZED_UPLOAD_MAX_PIXELS_DEFAULT,
  SIZE_DISPLAY_DECIMALS,
  SIZE_PRICE_MODES,
  SWATCH_SIZES,
  SWATCH_SIZE_PX,
  TEXT_TRANSFORMS,
  TRIM_MODES,
  UPLOAD_MAX_PIXELS_DEFAULT,
  UPLOAD_SOURCES,
  VARIANT_MATCH_RULES,
  activeQuantityTier,
  allowedMimesForOption,
  applyTextTransform,
  buildCheckoutPricingPayload,
  buildLineItemProperties,
  canonicalDeclaredMime,
  checkboxConfigSchema,
  checkboxGroupConfigSchema,
  checkoutQuantityError,
  customSizeConfigSchema,
  dateConfigSchema,
  decodeCheckoutSelections,
  detectedSizeIn,
  displayRuleConditionSchema,
  displayRuleSchema,
  displayRulesSchema,
  dpiQuality,
  effectiveMaxBytes,
  effectivePrintDpi,
  encodeCheckoutSelections,
  fitSize,
  formatInches,
  formatIsoDate,
  formatOrderedOptionValue,
  formulaRawCents,
  gangsheetFixedWidthIn,
  gangsheetVariantHeight,
  generateOptionSubmissionToken,
  generateOptionUploadToken,
  getOptionType,
  heightFromWidth,
  heightMatchesTrimmedRatio,
  infoConfigSchema,
  initialSelection,
  isFilled,
  isLegacyOptionType,
  isMimeAllowed,
  isOptionSelectionFilled,
  isOptionVisible,
  isPhase1OptionType,
  isProductOptionsEligible,
  isRealIsoDate,
  isSingleSelectOptionType,
  isSizedUploadOptionType,
  isStretchedSelection,
  isValidOptionUploadToken,
  isValidOptionsRefToken,
  listOptionTypes,
  matchVariantForSize,
  measuredPrintInches,
  monotonicProgress,
  normalizeChoices,
  normalizeMime,
  normalizePlatformMimeSuperset,
  numberConfigSchema,
  optionDisplayValue,
  optionSelectionSchema,
  optionSubmissionPayloadSchema,
  optionUploadDraftSchema,
  optionUploadFailureCode,
  optionUploadFailureMessage,
  optionUploadPreviewFiles,
  optionsBlockConfigSchema,
  orderedSizeText,
  orderedVariantLabel,
  partCountFor,
  physicalInches,
  pickerOptionTypesFor,
  pixelsPerMeterForInches,
  priceForSize,
  printSizeChangedNote,
  productOptionChoiceSchema,
  productOptionChoicesSchema,
  quantityConfigSchema,
  quantityTierSchema,
  recomputeCheckoutUnitCents,
  registerOptionType,
  resolveEffectiveDpi,
  resolveOptionPrice,
  resolveSizedUploadMaxPixels,
  resolveUploadMaxPixels,
  rewriteStoredUploadVersion,
  roundDisplay,
  sanitizeUploadFileName,
  selectedReplacementWidthIn,
  shopifyVariantNumericId,
  sizeFormulaMode,
  sizePresetSchema,
  sizeRateInches,
  sizedCheckoutDimension,
  sizedUploadEligibilityError,
  sizingBounds,
  stripDeclaredMime,
  swatchConfigSchema,
  swatchSizePx,
  textConfigSchema,
  unsafeCustomerPattern,
  uploadAcceptAttribute,
  uploadConfigSchema,
  uploadLinkProperties,
  uploadProgressPercent,
  validateSizedValues,
  variantAxisLabel,
  variantAxisSelectionSchema,
  visibleInOrder,
  widthFromHeight
};
//# sourceMappingURL=product-options.js.map